import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { SlidersHorizontal, X, RotateCcw, Church, MapPin, Navigation, Save, Trash2 } from "lucide-react";
import { getParishesList, getSavedLocation, saveLocation, clearSavedLocation } from "@/lib/api";
import { toast } from "sonner";

const CONDITIONS = [
  { value: "new", label: "New" },
  { value: "like_new", label: "Like New" },
  { value: "good", label: "Good" },
  { value: "fair", label: "Fair" }
];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "price_low", label: "Price: Low to High" },
  { value: "price_high", label: "Price: High to Low" },
  { value: "distance", label: "Nearest First" }
];

const DISTANCE_OPTIONS = [
  { value: "any", label: "Any Distance" },
  { value: "5", label: "Within 5 miles" },
  { value: "10", label: "Within 10 miles" },
  { value: "25", label: "Within 25 miles" },
  { value: "50", label: "Within 50 miles" },
  { value: "100", label: "Within 100 miles" },
  { value: "250", label: "Within 250 miles" }
];

const COUNTRIES = [
  { value: "us", label: "United States" },
  { value: "ca", label: "Canada" },
  { value: "gb", label: "United Kingdom" },
  { value: "au", label: "Australia" },
  { value: "ie", label: "Ireland" },
  { value: "ph", label: "Philippines" },
  { value: "mx", label: "Mexico" },
  { value: "br", label: "Brazil" },
  { value: "it", label: "Italy" },
  { value: "es", label: "Spain" },
  { value: "fr", label: "France" },
  { value: "de", label: "Germany" },
  { value: "pl", label: "Poland" }
];

export function AdvancedFilters({ filters, onFiltersChange, categories }) {
  const [parishes, setParishes] = useState([]);
  const [open, setOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);
  const [savedLocation, setSavedLocation] = useState(null);
  const [savingLocation, setSavingLocation] = useState(false);

  useEffect(() => {
    loadParishes();
    loadSavedLocation();
  }, []);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const loadParishes = async () => {
    try {
      const res = await getParishesList();
      setParishes(res.data);
    } catch {}
  };

  const loadSavedLocation = async () => {
    try {
      const res = await getSavedLocation();
      if (res.data.zip_code) {
        setSavedLocation(res.data);
      }
    } catch {}
  };

  const handleSaveLocation = async () => {
    if (!localFilters.userZip) {
      toast.error("Enter a zip code first");
      return;
    }
    setSavingLocation(true);
    try {
      await saveLocation({
        zip_code: localFilters.userZip,
        country: localFilters.userCountry || "us"
      });
      setSavedLocation({
        zip_code: localFilters.userZip,
        country: localFilters.userCountry || "us"
      });
      toast.success("Location saved! It will be remembered for future searches.");
    } catch {
      toast.error("Failed to save location");
    }
    setSavingLocation(false);
  };

  const handleClearSavedLocation = async () => {
    try {
      await clearSavedLocation();
      setSavedLocation(null);
      toast.success("Saved location cleared");
    } catch {
      toast.error("Failed to clear location");
    }
  };

  const handleUseSavedLocation = () => {
    if (savedLocation) {
      setLocalFilters({
        ...localFilters,
        userZip: savedLocation.zip_code,
        userCountry: savedLocation.country
      });
      toast.success("Using your saved location");
    }
  };

  const handleApply = () => {
    onFiltersChange(localFilters);
    setOpen(false);
  };

  const handleReset = () => {
    const defaultFilters = {
      category: "all",
      condition: "all",
      minPrice: "",
      maxPrice: "",
      parishId: "all",
      sortBy: "newest",
      listingType: "all",
      userZip: "",
      userCountry: "us",
      maxDistance: "any"
    };
    setLocalFilters(defaultFilters);
    onFiltersChange(defaultFilters);
    setOpen(false);
  };

  const activeFilterCount = Object.entries(filters).filter(([key, value]) => {
    if (key === "sortBy") return value !== "newest";
    return value && value !== "all" && value !== "";
  }).length;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button 
          variant="outline" 
          className="border-[#E5E0D5] text-[#2D2A26] font-['Nunito'] text-sm relative"
          data-testid="advanced-filters-btn"
        >
          <SlidersHorizontal className="w-4 h-4 mr-2" />
          Filters
          {activeFilterCount > 0 && (
            <Badge className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center bg-[var(--liturgical-primary)] text-white text-[10px]">
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="bg-white border-[#E5E0D5] w-[340px] flex flex-col">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26]">
            Advanced Filters
          </SheetTitle>
        </SheetHeader>
        
        <div className="flex-1 overflow-y-auto space-y-5 mt-4 pr-2">
          {/* Sort By */}
          <div className="space-y-2">
            <Label className="font-['Nunito'] text-sm text-[#2D2A26]">Sort By</Label>
            <Select 
              value={localFilters.sortBy || "newest"} 
              onValueChange={(v) => setLocalFilters({ ...localFilters, sortBy: v })}
            >
              <SelectTrigger className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="filter-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#E5E0D5]">
                {SORT_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value} className="font-['Nunito'] text-sm">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label className="font-['Nunito'] text-sm text-[#2D2A26]">Category</Label>
            <Select 
              value={localFilters.category || "all"} 
              onValueChange={(v) => setLocalFilters({ ...localFilters, category: v })}
            >
              <SelectTrigger className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="filter-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#E5E0D5]">
                <SelectItem value="all" className="font-['Nunito'] text-sm">All Categories</SelectItem>
                {categories.map(c => (
                  <SelectItem key={c} value={c} className="font-['Nunito'] text-sm">{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Condition */}
          <div className="space-y-2">
            <Label className="font-['Nunito'] text-sm text-[#2D2A26]">Condition</Label>
            <Select 
              value={localFilters.condition || "all"} 
              onValueChange={(v) => setLocalFilters({ ...localFilters, condition: v })}
            >
              <SelectTrigger className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="filter-condition">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#E5E0D5]">
                <SelectItem value="all" className="font-['Nunito'] text-sm">Any Condition</SelectItem>
                {CONDITIONS.map(c => (
                  <SelectItem key={c.value} value={c.value} className="font-['Nunito'] text-sm">
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Price Range */}
          <div className="space-y-2">
            <Label className="font-['Nunito'] text-sm text-[#2D2A26]">Price Range</Label>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <Input
                  type="number"
                  placeholder="Min"
                  value={localFilters.minPrice || ""}
                  onChange={(e) => setLocalFilters({ ...localFilters, minPrice: e.target.value })}
                  className="border-[#E5E0D5] font-['Nunito'] text-sm"
                  data-testid="filter-min-price"
                />
              </div>
              <span className="text-[#6B665E]">-</span>
              <div className="flex-1">
                <Input
                  type="number"
                  placeholder="Max"
                  value={localFilters.maxPrice || ""}
                  onChange={(e) => setLocalFilters({ ...localFilters, maxPrice: e.target.value })}
                  className="border-[#E5E0D5] font-['Nunito'] text-sm"
                  data-testid="filter-max-price"
                />
              </div>
            </div>
          </div>

          {/* Parish Filter */}
          <div className="space-y-2">
            <Label className="font-['Nunito'] text-sm text-[#2D2A26] flex items-center gap-1">
              <Church className="w-3.5 h-3.5" /> Parish
            </Label>
            <Select 
              value={localFilters.parishId || "all"} 
              onValueChange={(v) => setLocalFilters({ ...localFilters, parishId: v })}
            >
              <SelectTrigger className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="filter-parish">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#E5E0D5]">
                <SelectItem value="all" className="font-['Nunito'] text-sm">All Parishes</SelectItem>
                {parishes.map(p => (
                  <SelectItem key={p.parish_id} value={p.parish_id} className="font-['Nunito'] text-sm">
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Distance Filter */}
          <div className="space-y-3 pt-2 border-t border-[#E5E0D5]">
            <div className="flex items-center justify-between">
              <Label className="font-['Nunito'] text-sm text-[#2D2A26] flex items-center gap-1">
                <Navigation className="w-3.5 h-3.5" /> Distance Filter
              </Label>
              {savedLocation && (
                <Badge 
                  variant="outline" 
                  className="text-[10px] border-green-500/30 text-green-700 bg-green-50 cursor-pointer hover:bg-green-100"
                  onClick={handleUseSavedLocation}
                  data-testid="use-saved-location"
                >
                  <MapPin className="w-2.5 h-2.5 mr-1" />
                  Use saved: {savedLocation.zip_code}
                </Badge>
              )}
            </div>
            
            {/* User Zip Code */}
            <div className="space-y-1.5">
              <Label className="font-['Nunito'] text-xs text-[#6B665E]">Your Zip/Postal Code</Label>
              <Input
                placeholder="Enter your zip code"
                value={localFilters.userZip || ""}
                onChange={(e) => setLocalFilters({ ...localFilters, userZip: e.target.value })}
                className="border-[#E5E0D5] font-['Nunito'] text-sm"
                data-testid="filter-user-zip"
              />
            </div>
            
            {/* User Country */}
            <div className="space-y-1.5">
              <Label className="font-['Nunito'] text-xs text-[#6B665E]">Your Country</Label>
              <Select 
                value={localFilters.userCountry || "us"} 
                onValueChange={(v) => setLocalFilters({ ...localFilters, userCountry: v })}
              >
                <SelectTrigger className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="filter-user-country">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#E5E0D5] max-h-60">
                  {COUNTRIES.map(c => (
                    <SelectItem key={c.value} value={c.value} className="font-['Nunito'] text-sm">
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Max Distance */}
            <div className="space-y-1.5">
              <Label className="font-['Nunito'] text-xs text-[#6B665E]">Maximum Distance</Label>
              <Select 
                value={localFilters.maxDistance || "any"} 
                onValueChange={(v) => setLocalFilters({ ...localFilters, maxDistance: v })}
              >
                <SelectTrigger className="border-[#E5E0D5] font-['Nunito'] text-sm" data-testid="filter-max-distance">
                  <SelectValue placeholder="Any Distance" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#E5E0D5]">
                  {DISTANCE_OPTIONS.map(d => (
                    <SelectItem key={d.value} value={d.value} className="font-['Nunito'] text-sm">
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Save/Clear Location Buttons */}
            {localFilters.userZip && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSaveLocation}
                  disabled={savingLocation}
                  className="flex-1 text-xs border-[#E5E0D5] text-[#2D2A26] font-['Nunito']"
                  data-testid="save-location-btn"
                >
                  <Save className="w-3 h-3 mr-1" />
                  {savingLocation ? "Saving..." : "Save my location"}
                </Button>
                {savedLocation && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearSavedLocation}
                    className="text-xs border-red-200 text-red-600 hover:bg-red-50 font-['Nunito']"
                    data-testid="clear-location-btn"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            )}
            
            {!localFilters.userZip && localFilters.maxDistance && localFilters.maxDistance !== "any" && (
              <p className="text-xs text-amber-600 font-['Nunito']">
                Enter your zip code to enable distance filtering
              </p>
            )}
          </div>
        </div>

        {/* Actions - Fixed at bottom */}
        <div className="flex-shrink-0 flex gap-2 pt-4 mt-auto border-t border-[#E5E0D5]">
          <Button 
            variant="outline" 
            className="flex-1 border-[#E5E0D5] font-['Nunito']"
            onClick={handleReset}
            data-testid="filter-reset-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" /> Reset
          </Button>
          <Button 
            className="flex-1 bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
            onClick={handleApply}
            data-testid="filter-apply-btn"
          >
            Apply Filters
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function ActiveFilters({ filters, onRemoveFilter, categories }) {
  const activeFilters = [];
  
  if (filters.category && filters.category !== "all") {
    activeFilters.push({ key: "category", label: filters.category, value: filters.category });
  }
  if (filters.condition && filters.condition !== "all") {
    const condLabel = CONDITIONS.find(c => c.value === filters.condition)?.label || filters.condition;
    activeFilters.push({ key: "condition", label: condLabel, value: filters.condition });
  }
  if (filters.minPrice) {
    activeFilters.push({ key: "minPrice", label: `Min: $${filters.minPrice}`, value: filters.minPrice });
  }
  if (filters.maxPrice) {
    activeFilters.push({ key: "maxPrice", label: `Max: $${filters.maxPrice}`, value: filters.maxPrice });
  }
  if (filters.parishId && filters.parishId !== "all") {
    activeFilters.push({ key: "parishId", label: "Parish Filter", value: filters.parishId });
  }
  if (filters.sortBy && filters.sortBy !== "newest") {
    const sortLabel = SORT_OPTIONS.find(s => s.value === filters.sortBy)?.label || filters.sortBy;
    activeFilters.push({ key: "sortBy", label: sortLabel, value: filters.sortBy });
  }
  if (filters.userZip && filters.maxDistance && filters.maxDistance !== "any") {
    const distLabel = DISTANCE_OPTIONS.find(d => d.value === filters.maxDistance)?.label || `${filters.maxDistance} mi`;
    activeFilters.push({ key: "distance", label: `📍 ${distLabel}`, value: filters.maxDistance });
  }

  if (activeFilters.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 mb-4" data-testid="active-filters">
      {activeFilters.map(({ key, label }) => (
        <Badge 
          key={key}
          variant="secondary" 
          className="bg-[var(--liturgical-primary)]/10 text-[var(--liturgical-primary)] border border-[var(--liturgical-primary)]/20 font-['Nunito'] text-xs flex items-center gap-1 pr-1"
        >
          {label}
          <button
            onClick={() => onRemoveFilter(key)}
            className="ml-1 hover:bg-[var(--liturgical-primary)]/20 rounded-full p-0.5"
            data-testid={`remove-filter-${key}`}
          >
            <X className="w-3 h-3" />
          </button>
        </Badge>
      ))}
      <button
        onClick={() => onRemoveFilter("all")}
        className="text-xs text-[#6B665E] hover:text-[var(--liturgical-primary)] font-['Nunito'] underline"
        data-testid="clear-all-filters"
      >
        Clear all
      </button>
    </div>
  );
}
